import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { fetchMastodonLists } from '@/services/mastodonService'
import { getMastodonAccountId } from '@/lib/mastodon'
import type { MastodonList } from '@/types/lists'

export function useMastodonLists() {
    const { mastodonSession } = useAuth()
    // Account IDs are instance-local — two accounts on different instances can share the
    // same numeric id. Scope the query key by the full account identity so their cached
    // list data never collides.
    const accountScope = mastodonSession ? getMastodonAccountId(mastodonSession) : null

    const query = useQuery<MastodonList[], Error>({
        queryKey: ['mastodonLists', accountScope],
        queryFn: async () => {
            if (!mastodonSession) throw new Error('No Mastodon session')
            return fetchMastodonLists(mastodonSession)
        },
        enabled: !!mastodonSession,
        staleTime: 5 * 60 * 1000,
    })

    return {
        lists: query.data ?? [],
        hasLists: (query.data?.length ?? 0) > 0,
        isLoading: query.isLoading,
        // Sticky "the query has produced a result (success or error) at least once" signal.
        // React Query's isLoading flips back to true whenever a *new* observer mounts on an
        // errored query with no cached data (e.g. an initial-fetch error): such a query is
        // stale, so refetchOnMount refetches it, returning status to 'pending'. Page-level
        // loading gates must key off hasSettled instead, or mounting a child that also
        // observes this query re-raises the gate, unmounts that child, and loops forever
        // (issue #2118).
        hasSettled: query.isFetched,
        // Authority signal for reconciliation: do we have a trustworthy lists snapshot?
        // That's query.data !== undefined, not query.isSuccess alone — in TanStack Query v5,
        // a failed *background* refetch (e.g. window refocus after staleTime) flips status to
        // 'error' while query.data still holds the last successful result. An initial fetch
        // error has no data (not authoritative); a background refetch error with cached data
        // is still authoritative; an empty-list success sets data to [] (authoritative). Never
        // derive authority from !isLoading or hasLists — both would either mask transient
        // errors or permanently disable mutations for zero-list accounts.
        isSuccess: query.isSuccess || query.data !== undefined,
        refetch: query.refetch,
        error: query.error,
    }
}
